import React, { FC, memo, useContext } from 'react'

import { isToday, isTomorrow } from 'date-fns'
import { format, utcToZonedTime } from 'date-fns-tz'

import { AppContext } from 'Const'
import { CartFulfillment } from 'Types'
import { isDelivery } from 'Utils/cartFulfillment'

interface Props {
  fulfillment: CartFulfillment
  isASAP: boolean
}

const DELIVERY_INTERVAL_IN_MSECS = 30 * 60 * 1000

const FulfillmentDateTime: FC<Props> = ({ fulfillment, isASAP }) => {
  const {
    currentLocation: { timezone },
  } = useContext(AppContext)
  const formatTimeMoment = (timeMoment: Date, formatStr: string) =>
    format(timeMoment, formatStr, { timeZone: timezone })
  const formatFulfillmentAt = (formatStr: string) => formatTimeMoment(fulfillmentZonedDate, formatStr)

  const fulfillmentDate = convertStrToDate(fulfillment.currentFulfillmentAt as string)
  const fulfillmentZonedDate = utcToZonedTime(fulfillmentDate, timezone)
  const dayStr = isToday(fulfillmentZonedDate)
    ? `Today (${formatFulfillmentAt('MMM d')})`
    : isTomorrow(fulfillmentZonedDate)
    ? `Tomorrow (${formatFulfillmentAt('MMM d')})`
    : formatFulfillmentAt('E, MMM d')

  let timeStr
  if (isDelivery(fulfillment)) {
    const { scheduleSetting } = fulfillment
    if (scheduleSetting.disableTimeSelector && scheduleSetting.fulfillmentOperatingDay) {
      const operatingHours = scheduleSetting.fulfillmentOperatingDay.operatingHours
      const currentOperatingDayOpens = operatingHours[0].opens
      const currentOperatingDayCloses = operatingHours[operatingHours.length - 1].closes

      const currentZonedOperatingDayOpens = minutesSinceMidnightToDate(currentOperatingDayOpens)
      const currentZonedOperatingDayCloses = minutesSinceMidnightToDate(currentOperatingDayCloses)
      timeStr = `${formatTimeMoment(currentZonedOperatingDayOpens, 'h:mm a')} - ${formatTimeMoment(
        currentZonedOperatingDayCloses,
        'h:mm a',
      )}`
    } else {
      timeStr = `${formatFulfillmentAt('h:mm a')} - ${formatTimeMoment(
        endOfIntervalZonedTime(fulfillmentDate, timezone),
        'h:mm a',
      )}`
    }
  } else {
    timeStr = formatFulfillmentAt('h:mm a')
  }

  return (
    <>
      {dayStr} at {isASAP ? `ASAP (Approximately at ${timeStr})` : timeStr}
    </>
  )
}

// + 30 minutes
function endOfIntervalZonedTime(startOfIntervalAt: Date, timezone: string): Date {
  return utcToZonedTime(new Date(startOfIntervalAt.getTime() + DELIVERY_INTERVAL_IN_MSECS), timezone)
}

function convertStrToDate(strDate: string): Date {
  return new Date(strDate)
}

function minutesSinceMidnightToDate(minutesSinceMidnight: number): Date {
  const date = new Date(0, 0)
  date.setMinutes(minutesSinceMidnight)
  return date
}

export default memo(FulfillmentDateTime)
